import type { GroupLifeTarifacaoLife } from '@/services/group-life';
import { resolveInsurableGroupLabel } from '@/features/quotes/data/group-life/insurable-groups';
import { formatCurrency } from '@/shared/utils';
import {
  Text,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { ChevronRight } from 'lucide-react';

const DASH = '—';

/** `YYYY-MM-DD` (como vem do InsureMO) → `DD/MM/AAAA`. */
export function formatLifeBirthDate(value?: string): string {
  const iso = (value ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return value?.trim() || DASH;
}

function genderLabel(gender?: string): string {
  return gender?.trim() || DASH;
}

type Options = {
  // eslint-disable-next-line no-unused-vars
  onExpand: (life: GroupLifeTarifacaoLife) => void;
};

/**
 * Colunas da "Lista de Vidas" da apólice PME.
 *
 * Não reaproveita `createLivesColumns` da proposta: lá as linhas são
 * `PmeLifeRow` (vida em edição, com status/CRUD) e aqui são
 * `GroupLifeTarifacaoLife` (leitura, com prêmio por vida). O mockup também
 * troca as colunas de STATUS/AÇÕES por um chevron de expandir e uma coluna
 * PRÊMIO.
 *
 * `sortable: false` em todas: o `.../pricing` do BFF não aceita parâmetro de
 * ordenação e a lista é paginada no servidor — ordenar só a página visível
 * daria um resultado enganoso.
 */
export function createPmePolicyLivesColumns({
  onExpand,
}: Options): DataTableColumn<GroupLifeTarifacaoLife>[] {
  return [
    {
      id: 'expand',
      header: '',
      size: 48,
      sortable: false,
      cell: ({ row }) => (
        <button
          type='button'
          aria-label={`Ver detalhes de ${row.original.name || 'vida'}`}
          onClick={() => onExpand(row.original)}
          className='flex cursor-pointer items-center justify-center border-none bg-transparent p-1 text-alba-gray-600'
        >
          <ChevronRight className='h-4 w-4' />
        </button>
      ),
    },
    {
      accessorKey: 'insurableGroup',
      header: 'GRUPO SEGURÁVEL',
      size: 180,
      sortable: false,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {row.original.insurableGroup
            ? resolveInsurableGroupLabel(row.original.insurableGroup)
            : DASH}
        </Text>
      ),
    },
    {
      accessorKey: 'capital',
      header: 'CAPITAL',
      size: 150,
      sortable: false,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {row.original.capital || DASH}
        </Text>
      ),
    },
    {
      accessorKey: 'gender',
      header: 'SEXO',
      size: 80,
      sortable: false,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {genderLabel(row.original.gender)}
        </Text>
      ),
    },
    {
      accessorKey: 'birthDate',
      header: 'DATA NASCIMENTO',
      size: 160,
      sortable: false,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {formatLifeBirthDate(row.original.birthDate)}
        </Text>
      ),
    },
    {
      accessorKey: 'name',
      header: 'NOME',
      size: 200,
      sortable: false,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {row.original.name || DASH}
        </Text>
      ),
    },
    {
      accessorKey: 'totalDuePremium',
      header: 'PRÊMIO',
      size: 140,
      sortable: false,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {formatCurrency(row.original.totalDuePremium ?? 0)}
        </Text>
      ),
    },
  ];
}
